// frontend/src/hooks/usePricing.ts
//
// Hook for fetching subscription pricing from Stripe.
// Caches the response for the session.

import { useState, useEffect, useCallback } from 'react';

export interface PriceInfo {
  id: string;
  amount: number;       // in cents
  currency: string;
  interval: 'month' | 'year';
  savings?: number;     // yearly savings in cents
}

export interface PlanInfo {
  tier: string;
  name: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  subscriptionCredits: number | null;
  prices: {
    monthly?: PriceInfo;
    yearly?: PriceInfo;
  };
}

export interface PaygPackage {
  credits: number;
  pricePerCredit: number;  // in cents
  price: {
    id: string;
    amount: number;        // in cents
    currency: string;
  };
  name: string;
}

interface UsePricingReturn {
  plans: PlanInfo[];
  paygPackages: PaygPackage[];
  paymentsDisabled: boolean;
  message: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Cache pricing data for the session
let cachedPlans: PlanInfo[] | null = null;
let cachedPaygPackages: PaygPackage[] | null = null;
let cachedPaymentsDisabled = false;
let cachedMessage: string | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function usePricing(): UsePricingReturn {
  const [plans, setPlans] = useState<PlanInfo[]>(cachedPlans || []);
  const [paygPackages, setPaygPackages] = useState<PaygPackage[]>(cachedPaygPackages || []);
  const [paymentsDisabled, setPaymentsDisabled] = useState(cachedPaymentsDisabled);
  const [message, setMessage] = useState<string | null>(cachedMessage);
  const [loading, setLoading] = useState(!cachedPlans);
  const [error, setError] = useState<string | null>(null);

  const fetchPricing = useCallback(async (force = false) => {
    // Use cache if valid and not forced
    if (!force && cachedPlans && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setPlans(cachedPlans);
      setPaygPackages(cachedPaygPackages || []);
      setPaymentsDisabled(cachedPaymentsDisabled);
      setMessage(cachedMessage);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-pricing`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch pricing');
      }

      const data = await response.json();
      
      // Update cache
      cachedPlans = data.plans || [];
      cachedPaygPackages = data.paygPackages || [];
      cachedPaymentsDisabled = data.paymentsDisabled === true;
      cachedMessage = data.message || null;
      cacheTimestamp = Date.now();
      
      setPlans(data.plans || []);
      setPaygPackages(data.paygPackages || []);
      setPaymentsDisabled(data.paymentsDisabled === true);
      setMessage(data.message || null);
    } catch (err) {
      console.error('Error fetching pricing:', err);
      setError(err instanceof Error ? err.message : 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const refetch = useCallback(async () => {
    await fetchPricing(true);
  }, [fetchPricing]);

  return {
    plans,
    paygPackages,
    paymentsDisabled,
    message,
    loading,
    error,
    refetch,
  };
}

/**
 * Format price in cents to display string
 */
export function formatPrice(amount: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100);
}

/**
 * Get display name for a tier
 */
export function getTierDisplayName(tier: string): string {
  const names: Record<string, string> = {
    free: 'Free',
    standard_with_ads: 'Standard with Ads',
    standard: 'Standard',
    premium: 'Premium',
  };
  return names[tier] || tier;
}
